import { useContext } from 'react';
import { label, linkLabel, plural } from '@entities/ksp/format';
import type {
  Activity,
  ActivityTiming,
  KspAction,
  ScheduleVersion,
  KspWorkspace,
} from '@entities/ksp/types';
import type { FormField } from '@features/ksp-actions/ActionForm';
import { Gantt } from '@widgets/ksp-workspace/Gantt';
import { dayStart, localDate } from '@widgets/ksp-workspace/gantt/model';
import { Button } from '@shared/ui/Button';
import { Card, Row, Select, Small } from '../styles';
import { CollectionContext, choice, dateField, textField } from '../types';
import { PagedTable } from '../components/CollectionTable';

export interface ScheduleTabProps {
  workspace: KspWorkspace;
  projectId: string;
  versionId?: string | null;
  version?: ScheduleVersion;
  timezone: string;
  editable: boolean;
  permission: (action?: KspAction) => boolean;
  open: (
    title: string,
    fields: FormField[],
    action: (v: Record<string, string>) => Promise<unknown>,
    submit?: string,
  ) => void;
  execute: (
    path: string,
    body: Record<string, unknown>,
    method?: 'POST' | 'PATCH' | 'DELETE',
  ) => Promise<unknown>;
  setMessage: (msg: string) => void;
  activityForm: (activity?: Activity) => void;
  workOptions: { value: string; label: string }[];
  calendarOptions: { value: string; label: string }[];
  wbsOptions: { value: string; label: string }[];
  fileOptions: { value: string; label: string }[];
  timestamps: ActivityTiming[];
  params: URLSearchParams;
  setParams: (params: URLSearchParams) => void;
}

export function ScheduleTab({
  workspace: d,
  projectId,
  versionId,
  version,
  timezone,
  editable,
  permission,
  open,
  execute,
  setMessage,
  activityForm,
  workOptions,
  calendarOptions,
  wbsOptions,
  fileOptions,
  timestamps,
  params,
  setParams,
}: ScheduleTabProps) {
  const collections = useContext(CollectionContext);
  /** Что затронет перенос: число работ после неё по связям и вехи среди них. */
  const moveHelp = (affected: string[]) => {
    if (!affected.length) return 'После этой работы по связям ничего нет — сдвинется только она.';
    const byId = new Map(d.activities.map((a) => [a.id, a]));
    const milestones = affected
      .map((id) => byId.get(id))
      .filter((a) => a?.kind === 'milestone')
      .map((a) => a!.code);
    const n = affected.length;
    return `Пересчёт затронет ${n.toLocaleString('ru-RU')} ${plural(n, 'работу', 'работы', 'работ')} после неё по связям${
      milestones.length
        ? `, среди них ${milestones.length} ${plural(milestones.length, 'веха', 'вехи', 'вех')}: ${milestones.slice(0, 5).join(', ')}${milestones.length > 5 ? ` и ещё ${milestones.length - 5}` : ''}`
        : ''
    }.`;
  };
  return (
    <>
      <Card>
        <Row>
          <h3>Версии графика</h3>
          <Select
            aria-label="Версия графика"
            value={versionId ?? ''}
            onChange={(event) => {
              const next = new URLSearchParams(params);
              next.set('version', event.target.value);
              setParams(next);
            }}
          >
            {d.versions.map((v) => (
              <option key={v.id} value={v.id}>
                {label(v.kind)} · ред. {v.revision} · {label(v.approval)}
              </option>
            ))}
          </Select>
          <Button
            $size="sm"
            $variant="secondary"
            disabled={!permission()}
            onClick={() =>
              open(
                'Новая версия графика',
                [
                  textField('name', 'Название версии'),
                  choice(
                    'kind',
                    'Назначение',
                    ['contract', 'detailed_baseline', 'current', 'forecast', 'scenario'].map(
                      (value) => ({ value, label: label(value) }),
                    ),
                  ),
                  textField('basis', 'Основание создания'),
                ],
                (values) =>
                  execute(`projects/${projectId}/versions`, {
                    ...values,
                    parent_id: versionId,
                  }),
              )
            }
          >
            Новая версия
          </Button>
          <Button
            $size="sm"
            $variant="secondary"
            disabled={!editable || !versionId}
            onClick={() => activityForm()}
          >
            Добавить работу
          </Button>
          <Button
            $size="sm"
            $variant="secondary"
            disabled={!permission() || !versionId}
            onClick={() =>
              open(
                'Новая ревизия выбранного графика',
                [
                  textField('name', 'Название новой ревизии'),
                  textField('basis', 'Основание изменений'),
                ],
                (values) => execute(`versions/${versionId}/clone`, values),
              )
            }
          >
            Копировать версию
          </Button>
          <Button
            $size="sm"
            disabled={!versionId || !permission()}
            onClick={() =>
              open(
                'Рассчитать график',
                [
                  { ...textField('data_date', 'Дата состояния UTC'), type: 'datetime-local' },
                  choice(
                    'mode',
                    'Режим',
                    [
                      { value: 'cpm', label: 'CPM без ресурсного выравнивания' },
                      { value: 'leveled', label: 'Ресурсный прогноз' },
                    ],
                    'cpm',
                  ),
                ],
                (values) =>
                  execute(`versions/${versionId}/calculate`, {
                    level_resources: values.mode === 'leveled',
                    data_date: new Date(`${values.data_date}:00Z`).toISOString(),
                  }),
              )
            }
          >
            Рассчитать
          </Button>
          <Button
            $size="sm"
            $variant="secondary"
            disabled={!editable || !versionId}
            onClick={() =>
              open('Передать на согласование', [textField('basis', 'Основание')], (values) =>
                execute(`versions/${versionId}/submit`, values),
              )
            }
          >
            На согласование
          </Button>
          <Button
            $size="sm"
            $variant="secondary"
            disabled={
              !permission('approve') || !versionId || version?.approval !== 'internal_review'
            }
            onClick={() =>
              open(
                'Утвердить базовый график',
                [
                  choice('status', 'Вид утверждения', [
                    { value: 'approved_internal', label: 'Внутреннее утверждение' },
                    { value: 'approved_customer', label: 'Согласовано Заказчиком' },
                  ]),
                  textField('basis', 'Основание'),
                  choice('approval_file_id', 'Документ согласования', fileOptions),
                ],
                (values) => execute(`versions/${versionId}/approve`, values),
              )
            }
          >
            Утвердить
          </Button>
        </Row>
        <Gantt
          activities={d.activities}
          timings={timestamps}
          dependencies={d.dependencies}
          wbs={d.wbs}
          rdBindings={d.rd_bindings}
          rdIncomplete={!!d.collections?.rd_bindings?.truncated}
          onLoadRd={collections ? () => collections.load('rd_bindings') : undefined}
          incomplete={
            d.collections?.activities?.truncated && collections
              ? {
                  loaded: d.activities.length,
                  total: d.collections.activities.total,
                  onLoad: () => collections.load('activities'),
                }
              : null
          }
          timezone={timezone}
          readonlyReason={
            d.history_read_only
              ? 'открыт исторический срез'
              : !permission()
                ? 'нет прав на изменения в этом проекте'
                : !editable
                  ? 'версия утверждена — для изменений создайте новую ревизию'
                  : d.collections?.dependencies?.truncated
                    ? 'связи загружены не все'
                    : null
          }
          onEdit={(activity) =>
            editable
              ? activityForm(activity)
              : setMessage(
                  `Работа ${activity.code}: утверждённая версия доступна для чтения. Создайте новую ревизию для изменений.`,
                )
          }
          onMove={(activity, proposed, affected) =>
            open(
              `Перенос ${activity.code}`,
              [
                {
                  ...dateField(
                    'earliest_start',
                    'Новое начало (по времени объекта)',
                    localDate(Date.parse(proposed), timezone),
                  ),
                  help: moveHelp(affected),
                },
                textField('basis', 'Основание переноса'),
              ],
              async (values) => {
                const start = dayStart(values.earliest_start, timezone);
                if (!start) throw new Error('Укажите дату нового начала.');
                const changed = await execute(
                  `versions/${versionId}/activities/${activity.id}`,
                  {
                    ...activity,
                    ...values,
                    earliest_start: start,
                    constraints: {
                      ...activity.constraints,
                      earliest_start: start,
                    },
                    version_id: versionId,
                  },
                  'PATCH',
                );
                if (changed)
                  await execute(`versions/${versionId}/calculate`, {
                    expected_version: (changed as { version?: number }).version,
                    data_date: d.summary?.data_date ?? new Date().toISOString(),
                    level_resources: false,
                  });
              },
              'Применить и пересчитать',
            )
          }
        />
      </Card>
      <Card>
        <h3>Структура WBS</h3>
        <Button
          disabled={!editable || !versionId}
          onClick={() =>
            open(
              'Раздел WBS',
              [
                textField('code', 'Код раздела'),
                textField('name', 'Наименование'),
                {
                  ...choice('parent_id', 'Родительский раздел', wbsOptions),
                  required: false,
                },
              ],
              (values) =>
                execute(`versions/${versionId}/wbs`, {
                  ...values,
                  parent_id: values.parent_id || null,
                  version_id: versionId,
                }),
            )
          }
        >
          Добавить раздел
        </Button>
        <PagedTable
          rows={d.wbs ?? []}
          columns={[
            { name: 'Код', value: (node) => node.code },
            { name: 'Раздел', value: (node) => node.name },
            {
              name: 'Родитель',
              value: (node) =>
                d.wbs?.find((parent) => parent.id === node.parent_id)?.code ?? 'Корень',
            },
          ]}
        />
      </Card>
      <Card>
        <h3>Сетевые связи</h3>
        <Button
          disabled={!editable || !versionId}
          onClick={() =>
            open(
              'Новая связь',
              [
                choice('predecessor_id', 'Предшественник', workOptions),
                choice('successor_id', 'Последователь', workOptions),
                choice(
                  'kind',
                  'Тип зависимости',
                  ['FS', 'SS', 'FF', 'SF'].map((value) => ({ value, label: value })),
                  'FS',
                ),
                {
                  ...textField('lag_minutes', 'Лаг, минуты (отрицательный допустим)', true, '0'),
                  type: 'number',
                },
                choice(
                  'lag_kind',
                  'Смысл лага',
                  [
                    { value: 'working', label: 'Рабочие минуты календаря связи' },
                    { value: 'elapsed', label: 'Прошедшие UTC-минуты' },
                  ],
                  'working',
                ),
                choice('calendar_id', 'Календарь связи', calendarOptions),
              ],
              (values) =>
                execute(`versions/${versionId}/dependencies`, {
                  ...values,
                  version_id: versionId,
                  lag_minutes: Number(values.lag_minutes),
                }),
            )
          }
        >
          Добавить связь
        </Button>
        <PagedTable
          rows={d.dependencies}
          columns={[
            {
              name: 'Предшественник',
              value: (edge) => {
                const work = d.activities.find((a) => a.id === edge.predecessor_id);
                return <span title={work?.name}>{work?.code ?? '—'}</span>;
              },
            },
            {
              name: 'Последователь',
              value: (edge) => {
                const work = d.activities.find((a) => a.id === edge.successor_id);
                return <span title={work?.name}>{work?.code ?? '—'}</span>;
              },
            },
            {
              name: 'Связь и лаг',
              value: (edge) => (
                <span title={`${edge.kind}, лаг ${edge.lag_minutes} мин`}>{linkLabel(edge)}</span>
              ),
            },
            {
              name: 'Действие',
              value: (edge) => (
                <Button
                  $size="sm"
                  $variant="ghost"
                  disabled={!editable}
                  onClick={() =>
                    open('Удалить связь', [textField('basis', 'Основание')], (values) =>
                      execute(`versions/${versionId}/dependencies/${edge.id}`, values, 'DELETE'),
                    )
                  }
                >
                  Удалить
                </Button>
              ),
            },
          ]}
        />
      </Card>
      <Card>
        <h3>Рабочие календари</h3>
        <Button
          disabled={!editable}
          onClick={() =>
            open(
              'Новая версия календаря',
              [
                textField('name', 'Название'),
                textField('timezone', 'Часовой пояс', true, timezone),
                choice(
                  'week',
                  'Рабочая неделя',
                  [
                    { value: 'five', label: 'Понедельник–пятница' },
                    { value: 'seven', label: 'Все дни' },
                    { value: 'always', label: '24×7 без перерывов' },
                  ],
                  'five',
                ),
                { ...textField('start', 'Начало смены', true, '09:00'), type: 'time' },
                { ...textField('finish', 'Конец смены', true, '17:00'), type: 'time' },
                { ...textField('break_start', 'Начало перерыва', false), type: 'time' },
                { ...textField('break_end', 'Конец перерыва', false), type: 'time' },
                {
                  ...dateField('exception_date', 'Дата исключения (нерабочий день)'),
                  required: false,
                },
              ],
              (values) => {
                const minute = (value: string) =>
                  Number(value.slice(0, 2)) * 60 + Number(value.slice(3, 5));
                const weekly = Array.from(
                  { length: values.week === 'five' ? 5 : 7 },
                  (_, weekday) => {
                    if (values.week === 'always')
                      return [{ weekday, start_minute: 0, end_minute: 1440 }];
                    const start = minute(values.start),
                      end = minute(values.finish);
                    if (end < start)
                      return [
                        { weekday, start_minute: start, end_minute: 1440 },
                        { weekday: (weekday + 1) % 7, start_minute: 0, end_minute: end },
                      ];
                    if (values.break_start && values.break_end)
                      return [
                        {
                          weekday,
                          start_minute: start,
                          end_minute: minute(values.break_start),
                        },
                        { weekday, start_minute: minute(values.break_end), end_minute: end },
                      ];
                    return [{ weekday, start_minute: start, end_minute: end }];
                  },
                ).flat();
                return execute(`projects/${projectId}/calendars`, {
                  name: values.name,
                  timezone: values.timezone,
                  definition: {
                    weekly,
                    exceptions: values.exception_date ? { [values.exception_date]: [] } : {},
                  },
                });
              },
            )
          }
        >
          Добавить календарь
        </Button>
        <PagedTable
          rows={d.calendars}
          columns={[
            { name: 'Календарь', value: (c) => c.name ?? c.id },
            { name: 'Часовой пояс', value: (c) => c.timezone },
            { name: 'Версия', value: (c) => c.version },
            {
              name: 'Смены и исключения',
              value: (c) => (
                <>
                  {c.weekly.map((s, i) => (
                    <Small key={i}>
                      {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'][s.weekday]}{' '}
                      {Math.floor(s.start_minute / 60)}:
                      {String(s.start_minute % 60).padStart(2, '0')}–{Math.floor(s.end_minute / 60)}
                      :{String(s.end_minute % 60).padStart(2, '0')}
                    </Small>
                  ))}
                  {Object.keys(c.exceptions).map((day) => (
                    <Small key={day}>{day}: исключение</Small>
                  ))}
                </>
              ),
            },
          ]}
        />
      </Card>
      <Card>
        <h3>Расчёты</h3>
        <PagedTable
          rows={d.calculations}
          columns={[
            {
              name: 'Состояние',
              value: (run) => (
                <span
                  title={`Входы ${run.input_hash}${
                    run.output_hash
                      ? `
Результат ${run.output_hash}`
                      : ''
                  }`}
                >
                  {label(run.status)}
                  {run.error && <Small>{run.error}</Small>}
                  {run.warnings?.length ? (
                    <Small>
                      Замечаний: {run.warnings.length} ·{' '}
                      {run.warnings
                        .slice(0, 3)
                        .map((warning) => label(warning.code))
                        .join(', ')}
                    </Small>
                  ) : null}
                </span>
              ),
            },
            {
              name: 'Публикация',
              value: (run) => (
                <Button
                  $size="sm"
                  disabled={
                    !permission('publish') || !['succeeded', 'completed'].includes(run.status)
                  }
                  onClick={() =>
                    open(
                      'Опубликовать расчёт',
                      [textField('basis', 'Основание публикации')],
                      (values) => execute(`calculations/${run.id}/publish`, values),
                    )
                  }
                >
                  Опубликовать
                </Button>
              ),
            },
          ]}
        />
      </Card>
    </>
  );
}
